import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';

import { NamedLink, IconArrowHead } from '../../components';

import { hasChildren, categorySearchString } from './helpers';

import css from './CategoryFlyout.module.css';

/**
 * Single column of category links (Level 2 or Level 3). The first row
 * is always a "See all" link that targets the column's parent path.
 *
 * @param {Object} props
 * @param {Array<string>} props.basePath ids of ancestors (Level 1 only
 *                                       for the L2 column, Level 1 + 2
 *                                       for the L3 column)
 * @param {Node} props.allLabel translated "See all in {category}" node
 * @param {Array} props.items category entries to render in this column
 * @param {string|null} props.hoveredItemId for branch-row highlight
 * @param {Function} props.onHoverItem fires with the row id (or null)
 * @param {Function} props.onClickLink invoked after navigation so the
 *                                     parent menu can close itself
 * @param {string} [props.columnClassName] optional extra className
 */
const CategoryColumn = props => {
  const {
    basePath,
    allLabel,
    items,
    hoveredItemId,
    onHoverItem,
    onClickLink,
    columnClassName,
  } = props;

  // Level 3 always renders as a two-column grid (matches Vinted). The
  // L3 column is identified by basePath having two ancestor ids — the
  // Level 1 category and the Level 2 branch.
  const isLevelThree = basePath.length === 2;

  return (
    <ul
      className={classNames(css.column, columnClassName, {
        [css.columnTwoUp]: isLevelThree,
      })}
    >
      {/* NamedLink doesn't forward onClick/onMouseEnter to its inner
          Link element, so we attach those handlers to the wrapping
          <li> instead. Click bubbles up from the anchor; onMouseEnter
          fires directly on the row. */}
      <li className={css.rowWrap} onClick={onClickLink}>
        <NamedLink
          className={css.allLink}
          name="SearchPage"
          to={{ search: categorySearchString(basePath) }}
        >
          {allLabel}
        </NamedLink>
      </li>

      {items.map(item => {
        const itemPath = [...basePath, item.id];
        const itemHasChildren = hasChildren(item);
        return (
          <li
            key={item.id}
            className={css.rowWrap}
            onMouseEnter={() => onHoverItem(item.id)}
            onClick={onClickLink}
          >
            <NamedLink
              className={classNames(css.itemLink, {
                [css.itemLinkActive]: itemHasChildren && hoveredItemId === item.id,
              })}
              name="SearchPage"
              to={{ search: categorySearchString(itemPath) }}
            >
              <span className={css.itemLabel}>{item.name}</span>
              {itemHasChildren ? (
                <IconArrowHead direction="right" size="small" className={css.itemChevron} />
              ) : null}
            </NamedLink>
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Hover flyout shown beneath the CategoryMenu Level 1 row. Renders the
 * Level 2 of the active Level 1 in the first column; when the buyer
 * hovers a branch (Level 2 with children), the Level 3 column appears
 * to the right.
 *
 * Branch hover is sticky — the L3 column stays mounted until the buyer
 * either hovers another branch, hovers a leaf row, or leaves the
 * flyout altogether. That keeps the L3 column from flickering as the
 * mouse moves diagonally across the empty space between the L2 row and
 * its L3 panel.
 *
 * @param {Object} props
 * @param {Object} props.category active Level 1 category
 * @param {Function} props.onRequestClose called after a leaf click so
 *                                        the parent menu can dismiss
 */
const CategoryFlyout = props => {
  const { category, onRequestClose } = props;
  // Default the highlighted Level 2 row to the first branch (item with
  // children) so the Level 3 column is visible the moment the panel
  // opens — mirrors Vinted's behaviour. If no Level 2 has children
  // (e.g. Sko is flat), we start with null and no L3 column renders.
  // The parent passes `key={category.id}` so this initial state is
  // recomputed every time the user moves between Level 1 categories.
  const firstBranchId = category.subcategories.find(sc => hasChildren(sc))?.id || null;
  const [hoveredLevel2Id, setHoveredLevel2Id] = useState(firstBranchId);

  const handleHoverLevel2 = id => {
    const item = category.subcategories.find(sc => sc.id === id);
    if (item && hasChildren(item)) {
      setHoveredLevel2Id(id);
    } else {
      setHoveredLevel2Id(null);
    }
  };

  const handleClickLink = () => {
    if (typeof onRequestClose === 'function') {
      onRequestClose();
    }
  };

  const level2BasePath = [category.id];
  const level2AllLabel = (
    <FormattedMessage
      id="CategoryMenu.viewAllIn"
      values={{ category: category.name, quantifier: category.viewAllQuantifier }}
    />
  );

  const activeLevel2 = hoveredLevel2Id
    ? category.subcategories.find(sc => sc.id === hoveredLevel2Id) || null
    : null;
  const level3BasePath = activeLevel2 ? [category.id, activeLevel2.id] : null;
  const level3AllLabel = activeLevel2 ? (
    <FormattedMessage
      id="CategoryMenu.viewAllIn"
      values={{ category: activeLevel2.name, quantifier: activeLevel2.viewAllQuantifier }}
    />
  ) : null;

  return (
    <div className={css.flyout}>
      <div className={css.flyoutInner}>
        <CategoryColumn
          basePath={level2BasePath}
          allLabel={level2AllLabel}
          items={category.subcategories}
          hoveredItemId={hoveredLevel2Id}
          onHoverItem={handleHoverLevel2}
          onClickLink={handleClickLink}
        />

        {activeLevel2 ? (
          <CategoryColumn
            basePath={level3BasePath}
            allLabel={level3AllLabel}
            items={activeLevel2.subcategories}
            hoveredItemId={null}
            onHoverItem={() => null}
            onClickLink={handleClickLink}
            columnClassName={css.columnLevel3}
          />
        ) : null}
      </div>
    </div>
  );
};

export default CategoryFlyout;
